// BrandedVideoPlayer — premium custom-chrome wrapper around the
// native HTML5 <video> element for the client portal.
//
// What replaces the bare <video controls> default browser chrome:
//   • Center play/pause overlay with a soft frosted-glass disc
//   • Brand-color progress bar (replaces the gray default)
//   • Auto-hiding controls — fade out during playback after 2.5s
//     of mouse idle, fade back in on movement / pause
//   • Keyboard shortcuts: space toggles play, arrow keys ±5s, M
//     toggles mute, F toggles fullscreen
//   • Mobile-friendly tap targets (44px) for play/pause/fullscreen
//   • Time display (current / total)
//   • Volume slider on hover (desktop) / mute toggle (mobile)
//
// All wired to a single <video> ref via React. No external library
// — the standard HTMLMediaElement events (play, pause, timeupdate,
// loadedmetadata) are enough for a polished UX.
//
// Designed to drop into DeliverablesViewer. Studios who want even
// more (chapter markers, captions, watermarks) can graduate to
// the existing @mux/mux-player-react integration that's already
// in package.json — that's what we'd reach for at v1.1.

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
} from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { cn } from '@/lib/utils';

interface BrandedVideoPlayerProps {
  src: string;
  poster?: string | null;
  /// Studio's brand color — used for the progress bar fill,
  /// volume highlight, and any other accent. Defaults to a calm
  /// gold so the player still looks intentional even before the
  /// caller passes a color.
  brandColor?: string;
  /// Optional className for outer aspect-ratio container.
  className?: string;
}

const BrandedVideoPlayer = ({
  src,
  poster,
  brandColor = '#C9A16B',
  className,
}: BrandedVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0); // 0..1
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isScrubbing, setIsScrubbing] = useState(false);

  // Auto-hide controls during playback. Reset on every mouse move
  // / tap on the player. Stays visible while paused so the user
  // can always find the play button.
  const hideTimerRef = useRef<number | null>(null);
  const armHideTimer = useCallback(() => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      setControlsVisible(false);
    }, 2500);
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (playing) armHideTimer();
  }, [playing, armHideTimer]);

  // Wire native events to React state. Single effect because
  // they all share the same ref and lifecycle.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      setPlaying(true);
      armHideTimer();
    };
    const onPause = () => {
      setPlaying(false);
      setControlsVisible(true);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
    const onTimeUpdate = () => {
      if (!isScrubbing) {
        setCurrentTime(video.currentTime);
        setProgress(video.duration > 0 ? video.currentTime / video.duration : 0);
      }
    };
    const onLoadedMetadata = () => {
      setDuration(video.duration);
    };
    const onVolumeChange = () => {
      setMuted(video.muted);
      setVolume(video.volume);
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('volumechange', onVolumeChange);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('volumechange', onVolumeChange);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, [armHideTimer, isScrubbing]);

  // Track fullscreen state via document API so we can toggle
  // the icon between Maximize and Minimize. Safari needs the
  // webkit-prefixed event too.
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange as EventListener);
    };
  }, []);

  // Keyboard shortcuts. Bound to window with a focus check so we
  // don't hijack typing in inputs / textareas elsewhere on the
  // page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      if (e.key === ' ') {
        e.preventDefault();
        if (video.paused) video.play(); else video.pause();
      } else if (e.key === 'ArrowRight') {
        video.currentTime = Math.min(video.duration, video.currentTime + 5);
      } else if (e.key === 'ArrowLeft') {
        video.currentTime = Math.max(0, video.currentTime - 5);
      } else if (e.key.toLowerCase() === 'm') {
        video.muted = !video.muted;
      } else if (e.key.toLowerCase() === 'f') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
  };

  const toggleFullscreen = () => {
    const el = containerRef.current as HTMLDivElement & {
      webkitRequestFullscreen?: () => void;
    } | null;
    if (!el) return;
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
  };

  // Scrub: pointer-down anywhere on the progress bar starts
  // tracking; subsequent pointermove updates the seek position;
  // pointerup commits + resumes timeupdate-driven progress.
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const seekFromEvent = (clientX: number) => {
    const bar = progressBarRef.current;
    const video = videoRef.current;
    if (!bar || !video || !video.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setProgress(ratio);
    setCurrentTime(ratio * video.duration);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'group relative w-full aspect-video bg-black rounded-xl overflow-hidden',
        // Cursor hides with the controls — sleek but only on
        // pointer devices that support it.
        !controlsVisible && playing && 'cursor-none',
        className,
      )}
      onMouseMove={showControls}
      onMouseLeave={() => playing && setControlsVisible(false)}
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster ?? undefined}
        playsInline
        // We're providing our own controls; suppress the native
        // chrome entirely.
        className="w-full h-full"
      >
        Your browser does not support the video tag.
      </video>

      {/* Center play overlay — only when paused so it doesn't
          distract during playback. Solid dark disc (glass killed)
          for weight against any background. */}
      {!playing && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
          aria-label="Play"
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className="w-20 h-20 rounded-full bg-black/70 border border-white/20 flex items-center justify-center transition-transform group-hover:scale-105"
          >
            <OrbitIcon name="play.fill" fallback={Play} className="w-8 h-8 text-white fill-current ml-1" />
          </span>
        </button>
      )}

      {/* Bottom controls bar. Fades with `controlsVisible` so
          we get a clean image during silent playback. */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 transition-opacity duration-300',
          'bg-gradient-to-t from-black/85 via-black/50 to-transparent pt-10 pb-3 px-3',
          controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        // Stop bar clicks from bubbling up to the play-toggle div.
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar — full width, larger touch target on
            hover. Tap or drag to scrub. */}
        <div
          ref={progressBarRef}
          className="w-full h-1.5 hover:h-2 transition-all rounded-full bg-white/20 cursor-pointer relative mb-2"
          onPointerDown={(e) => {
            (e.target as Element).setPointerCapture(e.pointerId);
            setIsScrubbing(true);
            seekFromEvent(e.clientX);
          }}
          onPointerMove={(e) => {
            if (e.buttons > 0) seekFromEvent(e.clientX);
          }}
          onPointerUp={(e) => {
            (e.target as Element).releasePointerCapture(e.pointerId);
            setIsScrubbing(false);
          }}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-100"
            style={{
              width: `${progress * 100}%`,
              backgroundColor: brandColor,
            }}
          />
          {/* Scrub head — only visible on hover/scrub for a
              clean look at rest. */}
          <div
            className={cn(
              'absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full shadow-md',
              'opacity-0 group-hover:opacity-100 transition-opacity',
              isScrubbing && 'opacity-100',
            )}
            style={{
              left: `${progress * 100}%`,
              backgroundColor: brandColor,
            }}
          />
        </div>

        <div className="flex items-center gap-3 text-white">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? 'Pause' : 'Play'}
            className="w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
          >
            {playing ? (
              <OrbitIcon name="pause.fill" fallback={Pause} className="w-5 h-5 fill-current" />
            ) : (
              <OrbitIcon name="play.fill" fallback={Play} className="w-5 h-5 fill-current ml-0.5" />
            )}
          </button>

          {/* Volume cluster — mute button on mobile, button + slider
              on desktop. The slider lives in a hover popover so
              it never crowds the bar in idle state. */}
          <div className="flex items-center group/vol">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
              className="w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
            >
              {muted || volume === 0 ? (
                <OrbitIcon name="speaker.slash.fill" fallback={VolumeX} className="w-5 h-5" />
              ) : (
                <OrbitIcon name="speaker.wave.2.fill" fallback={Volume2} className="w-5 h-5" />
              )}
            </button>
            <div className="hidden sm:block w-0 group-hover/vol:w-20 overflow-hidden transition-all duration-200 ml-1">
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={muted ? 0 : volume}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (videoRef.current) {
                    videoRef.current.volume = v;
                    videoRef.current.muted = v === 0;
                  }
                }}
                className="w-full accent-white"
                aria-label="Volume"
              />
            </div>
          </div>

          <div className="text-xs tabular-nums text-white/85 ml-1">
            {formatTime(currentTime)} <span className="text-white/40">/</span> {formatTime(duration)}
          </div>

          <div className="flex-1" />

          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            className="w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
          >
            {isFullscreen ? (
              <OrbitIcon name="arrow.down.right.and.arrow.up.left" fallback={Minimize} className="w-5 h-5" />
            ) : (
              <OrbitIcon name="arrow.up.left.and.arrow.down.right" fallback={Maximize} className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/// Format seconds as M:SS (or H:MM:SS over an hour).
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default BrandedVideoPlayer;
